import { useState, useEffect, useRef } from 'react';

/**
 * useSelectHook - 외부 상태와 동기화되는 컨트롤드 훅
 *
 * @param {object} params
 * @param {Array} params.selected - 외부에서 관리하는 선택값
 * @param {Array} params.options - 전체 옵션
 * @param {boolean} params.multiple - 다중 선택 여부
 * @param {function} params.onChange - 선택값 변경 시 호출
 * @returns {object}
 */
export function useSelectHook({
  selected = [],
  options = [],
  multiple = false,
  onChange = () => {},
}) {
  const [selectedValues, setSelectedValues] = useState(selected);
  const [isOpen, setIsOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const popoverRef = useRef(null);

  // 깊은 비교
  const isEqual = (a, b) =>
    a.length === b.length && a.every((v) => b.includes(v));

  // 외부 selected가 변경되면 내부 상태도 동기화 (onChange는 호출하지 않음)
  useEffect(() => {
    if (!isEqual(selected, selectedValues)) {
      setSelectedValues(selected);
    }
    // eslint-disable-next-line
  }, [selected]);

  // selectAll 상태 갱신
  useEffect(() => {
    const allSelected = options.length > 0 && selectedValues.length === options.length;
    const noneSelected = selectedValues.length === 0;
    setSelectAll(allSelected || noneSelected);
  }, [selectedValues, options]);

  // 팝오버 열기/닫기
  const togglePopover = () => setIsOpen((prev) => !prev);

  // 항목 선택
  const handleSelect = (value) => {
    let updatedValues;

    if (multiple) {
      updatedValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];

      // 전체 선택되었으면 초기화
      if (updatedValues.length === options.length) {
        updatedValues = [];
      }
    } else {
      updatedValues = [value];
      setIsOpen(false);
    }

    setSelectedValues(updatedValues);
    onChange(updatedValues); // 여기서만 onChange 호출
  };

  // 전체 해제
  const handleSelectAll = () => {
    setSelectedValues([]);
    onChange([]); // 여기서도 onChange 호출
  };

  return {
    isOpen,
    setIsOpen,
    togglePopover,
    selectedValues,
    selectAll,
    handleSelect,
    handleSelectAll,
    popoverRef,
  };
}
